import styled, { keyframes } from 'styled-components';

const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 0.9; }
`;

/** Cor de destaque (barra lateral) por token — alinhado ao ecossistema de status */
export function statusTokenAccent(theme, token) {
    const t = theme?.colors || {};
    const map = {
        gray: t.gray?.[500] || '#6B7280',
        amber: t.warning?.main || '#D97706',
        blue: t.info?.main || '#2563EB',
        indigo: t.indigo?.main || '#6366F1',
        teal: t.teal?.main || '#14B8A6',
        orange: t.orange?.main || '#F97316',
        green: t.success?.main || '#16A34A',
        red: t.danger?.main || '#DC2626',
    };
    return map[token] || map.gray;
}

function statusTokenSurface(theme, token) {
    const t = theme?.colors || {};
    const map = {
        gray: t.gray?.[100] || '#E6E6E6',
        amber: t.warning?.light || '#FFFBEB',
        blue: t.info?.light || '#EFF6FF',
        indigo: t.indigo?.light || '#EEF2FF',
        teal: t.teal?.light || '#F0FDFA',
        orange: t.orange?.light || '#FFF7ED',
        green: t.success?.light || '#F0FDF4',
        red: t.danger?.light || '#FEE2E2',
    };
    return map[token] || map.gray;
}

export const IntroText = styled.p`
    margin: 0 0 16px;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.gray[500]};
    line-height: 1.45;
    max-width: 42rem;
`;

export const DeParaFlow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 20px;
    margin-bottom: 24px;
    padding: clamp(14px, 2.5vw, 18px) clamp(16px, 3vw, 20px);
    background: ${({ theme }) => theme.colors.background || '#F6F5FC'};
    border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.colors.gray[100] || '#E6E6E6'};
`;

export const FlowBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    flex: 0 1 auto;
`;

export const FlowLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.gray[500]};
`;

export const FlowArrow = styled.span`
    font-size: 1.35rem;
    font-weight: 300;
    color: ${({ theme }) => theme.colors.gray[500]};
    line-height: 1;
    user-select: none;
    flex-shrink: 0;
`;

export const PreviewPlaceholder = styled.span`
    font-size: 13px;
    color: ${({ theme }) => theme.colors.gray[500]};
    font-style: italic;
    line-height: 1.4;
    max-width: 14rem;
`;

export const SubsectionTitle = styled.h4`
    margin: 0 0 12px;
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.gray[900]};
`;

export const StatusOptionsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px;
    margin-bottom: 20px;
`;

export const StatusOptionTile = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    text-align: left;
    min-height: 48px;
    padding: 12px 14px 12px 18px;
    border-radius: 10px;
    border: 2px solid ${({ theme }) => theme.colors.gray[200]};
    background: #fff;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
    position: relative;
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.gray[900]};
    width: 100%;

    &::before {
        content: '';
        position: absolute;
        left: 6px;
        top: 10px;
        bottom: 10px;
        width: 4px;
        border-radius: 3px;
        background: ${({ theme, $colorToken }) => statusTokenAccent(theme, $colorToken)};
    }

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.colors.primary.main};
        outline-offset: 2px;
    }

    &:hover:not(:disabled) {
        border-color: ${({ theme }) => theme.colors.gray[200]};
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }

    &[aria-checked='true'] {
        border-color: ${({ theme, $colorToken }) => statusTokenAccent(theme, $colorToken)};
        background: ${({ theme, $colorToken }) => statusTokenSurface(theme, $colorToken)};
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.07);
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
        box-shadow: none;
    }

    svg {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
        color: ${({ theme, $colorToken }) => statusTokenAccent(theme, $colorToken)};
    }
`;

export const TileLabel = styled.span`
    flex: 1;
    min-width: 0;
    line-height: 1.35;
`;

export const LoadingGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px;
    margin-bottom: 20px;
`;

export const LoadingSkeleton = styled.div`
    height: 48px;
    border-radius: 10px;
    background: ${({ theme }) => theme.colors.gray[100] || '#E6E6E6'};
    animation: ${pulse} 1.1s ease-in-out infinite;
`;

export const MessageBox = styled.div`
    margin: 0 0 16px;
    padding: 12px 14px;
    font-size: 13px;
    line-height: 1.45;
    color: ${({ theme }) => theme.colors.gray[700]};
    background: #f9fafb;
    border-radius: 8px;
    border-left: 4px solid
        ${({ theme, $variant }) =>
            $variant === 'warning'
                ? theme.colors.warning?.main || '#D97706'
                : theme.colors.gray[400] || theme.colors.gray[500]};
`;

export const MessageBoxError = styled(MessageBox)`
    color: ${({ theme }) => theme.colors.gray[900]};
    border-left-color: ${({ theme }) => theme.colors.warning?.dark || '#92400E'};
    background: ${({ theme }) => theme.colors.warning?.light || '#FFFBEB'};
`;

export const ActionsRow = styled.div`
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 12px;
    padding-top: 4px;
    border-top: 1px solid ${({ theme }) => theme.colors.gray[100] || '#E6E6E6'};
    margin-top: 4px;
`;

/* ── Seção de fotos ──────────────────────────────────────────────────── */

export const PhotoSection = styled.div`
    margin-bottom: 20px;
`;

export const PhotoSectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
`;

export const PhotoSectionLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.gray[700]};
`;

export const PhotoCountBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 99px;
    background: ${({ theme, $warn }) =>
        $warn ? theme.colors.warning?.light || '#FFFBEB' : theme.colors.gray[100] || '#E6E6E6'};
    color: ${({ theme, $warn }) =>
        $warn ? theme.colors.warning?.dark || '#92400E' : theme.colors.gray[500]};
    border: 1px solid
        ${({ theme, $warn }) =>
            $warn ? theme.colors.warning?.main || '#D97706' : theme.colors.gray[200] || '#D1D5DB'};
    transition: background 0.2s, color 0.2s;
`;

export const PhotoGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: ${({ $hasItems }) => ($hasItems ? '10px' : '0')};
`;

export const PhotoThumbWrapper = styled.div`
    position: relative;
    width: 80px;
    height: 80px;
    border-radius: 8px;
    overflow: visible;
    flex-shrink: 0;
`;

export const PhotoThumbImg = styled.img`
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
    border: 1.5px solid ${({ theme }) => theme.colors.gray[200] || '#E5E7EB'};
    display: block;
`;

export const PhotoRemoveBtn = styled.button`
    position: absolute;
    top: -6px;
    right: -6px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1.5px solid #fff;
    background: ${({ theme }) => theme.colors.danger?.main || '#DC2626'};
    color: #fff;
    font-size: 11px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
    transition: background 0.15s;
    z-index: 1;

    &:hover {
        background: ${({ theme }) => theme.colors.danger?.dark || '#B91C1C'};
    }

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.colors.primary.main};
        outline-offset: 2px;
    }
`;

export const AddPhotoBtn = styled.button`
    width: 80px;
    height: 80px;
    border-radius: 8px;
    border: 1.5px dashed ${({ theme }) => theme.colors.gray[300] || '#D1D5DB'};
    background: ${({ theme }) => theme.colors.gray[50] || '#F9FAFB'};
    color: ${({ theme }) => theme.colors.gray[500]};
    font-size: 22px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: border-color 0.15s, background 0.15s, color 0.15s;

    &:hover:not(:disabled) {
        border-color: ${({ theme }) => theme.colors.primary.main};
        background: ${({ theme }) => theme.colors.primary.light || '#EEF2FF'};
        color: ${({ theme }) => theme.colors.primary.main};
    }

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.colors.primary.main};
        outline-offset: 2px;
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;
